import { FormEvent, ReactElement, startTransition, useRef, useState } from 'react';
import { useFormStatus } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  Link,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  FilledInput,
  FormHelperText,
  InputAdornment,
  IconButton,
  Typography,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

import { type State } from '@/types';
import { type LoginFields } from './LoginPage';
import { FrontPage } from './FrontPage';
import LanguageSelector from '@/components/common/LanguageSelector';

import './LoginPage.css';

function Submit() {
  const { pending } = useFormStatus();
  const { t } = useTranslation();

  return (
    <Button
      className="login-page-submit-button"
      disabled={pending}
      type="submit"
      fullWidth
      variant="contained"
      data-qa-element-id="login-submit-button"
    >
      {pending ? t('login.inProgress') : t('login.signIn')}
    </Button>
  );
}

function LoginInputForm({
  state,
  formAction,
  rememberMePreviousValue,
  canBiometric,
  startBiometric,
  resetPw,
  isPhone,
  initialUsername,
}: {
  state: State<LoginFields>;
  formAction: (payload: FormData) => void;
  rememberMePreviousValue: boolean;
  canBiometric: boolean;
  startBiometric?: () => void;
  resetPw: () => void;
  isPhone: boolean;
  initialUsername?: string;
}): ReactElement {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const ref = useRef<HTMLFormElement | null>(null);

  function handleClickShowPassword() {
    setShowPassword(!showPassword);
  }

  // changed action to this
  // so we opt out of form reset
  // https://github.com/facebook/react/issues/29034
  function handleSubmit(event: FormEvent<HTMLFormElement> | undefined) {
    event?.preventDefault();
    const formData = new FormData(ref.current!);
    startTransition(() => formAction(formData));
  }

  return (
    <>
      {!isPhone && (
        <div className="login-page-heading">
          <Typography variant="h3" color="primary.main" sx={{ py: 3 }} data-qa-element-id="login_page_title">
            {t('login.signIn')}
          </Typography>
          <Typography data-qa-element-id="for_registration_label">{t('registration.body')}</Typography>
        </div>
      )}
      <Box component="form" ref={ref} onSubmit={handleSubmit} noValidate>
        <FormControl
          className="login-page-field"
          variant="filled"
          margin="normal"
          fullWidth
          required
          autoFocus
          error={state.globalError !== undefined || state.errors.username !== undefined}
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: 'var(--mui-shape-borderRadius)',
            '& .MuiFormHelperText-root': {
              backgroundColor: '#fff',
              mx: 0,
              px: '14px',
              borderEndEndRadius: '3px',
              borderEndStartRadius: '3px',
              boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
            },
            '& .Mui-error::before': {
              borderWidth: 'thick',
            },
          }}
        >
          <InputLabel
            sx={{ color: 'white', fontWeight: 'bold' }}
            htmlFor="username"
            style={{ color: 'white' }}
            shrink={true}
          >
            {t('login.username')}
          </InputLabel>
          <FilledInput
            className="login-page-filled-input"
            id="username"
            name="username"
            type="username"
            autoComplete="username"
            defaultValue={initialUsername}
            sx={{ color: 'white' }}
            classes={{ input: 'login-page-filled-input-value' }}
            inputProps={{ autoCapitalize: 'none', 'data-qa-element-id': 'login-username-input' }}
            data-qa-element-id="login-username-div"
            endAdornment={
              <InputAdornment position="end" sx={{ p: 1 }}>
                <PersonIcon sx={{ color: 'white' }} fontSize="large" />
              </InputAdornment>
            }
          />
          <FormHelperText id="username-error-text" data-qa-element-id="login-username-error-text">
            {state.errors.username}
          </FormHelperText>
        </FormControl>

        <FormControl
          className="login-page-field"
          variant="filled"
          margin="normal"
          fullWidth
          required
          error={state.globalError !== undefined || state.errors.password !== undefined}
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: 'var(--mui-shape-borderRadius)',
            '& .MuiFormHelperText-root': {
              backgroundColor: '#fff',
              mx: 0,
              px: '14px',
              borderEndEndRadius: '3px',
              borderEndStartRadius: '3px',
              boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
            },
            '& .Mui-error::before': {
              borderWidth: 'thick',
            },
          }}
        >
          <InputLabel
            sx={{ color: 'white', fontWeight: 'bold' }}
            htmlFor="password"
            style={{ color: 'white' }}
            shrink={true}
          >
            {t('login.password')}
          </InputLabel>
          <FilledInput
            className="login-page-filled-input"
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            sx={{ color: 'white' }}
            classes={{ input: 'login-page-filled-input-value' }}
            inputProps={{ 'data-qa-element-id': 'login-password-input' }}
            data-qa-element-id="login-password-div"
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword}>
                  {showPassword ? (
                    <VisibilityIcon sx={{ color: 'white' }} fontSize="large" />
                  ) : (
                    <VisibilityOffIcon sx={{ color: 'white' }} fontSize="large" />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
          <FormHelperText id="password-error-text" data-qa-element-id="login-password-error-text">
            {state.errors.password}
          </FormHelperText>
        </FormControl>

        {state.globalError !== undefined && (
          <Typography variant="body2" className="login-page-error-title" data-qa-element-id="login_page_error_title">
            {state.globalError}!
          </Typography>
        )}

        <Box className="login-page-store-password-box" sx={{ justifyContent: 'space-between', width: '100%' }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  defaultChecked={rememberMePreviousValue}
                  slotProps={{
                    input: {
                      // @ts-expect-error this works but gives error :(
                      'data-qa-element-id': 'remember-me-checkbox',
                    },
                  }}
                />
              }
              label={<Typography data-qa-element-id="login-remember-me-label">{t('login.rememberMe')}</Typography>}
            />
          </FormGroup>
          {canBiometric && (
            <IconButton
              size="large"
              aria-label="biometric login"
              onClick={startBiometric}
              data-qa-element-id="biometric_button"
            >
              <FingerprintIcon fontSize="large" />
            </IconButton>
          )}
        </Box>

        <Submit />

        <Box className="login-page-secondary-actions">
          <LanguageSelector />
          <Box className="login-page-forgot-password login-page-forgot-password-inline">
            <Link
              component="button"
              onClick={resetPw}
              underline="none"
              variant="body1"
              data-qa-element-id="login-forgot-password-button"
            >
              {t('login.forgotPassword')}
            </Link>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export function LoginForm({
  isPhone,
  state,
  formAction,
  rememberMePreviousValue,
  canBiometric,
  startBiometric,
  resetPw,
  initialUsername,
}: {
  isPhone: boolean;
  state: State<LoginFields>;
  formAction: (payload: FormData) => void;
  rememberMePreviousValue: boolean;
  canBiometric: boolean;
  startBiometric?: () => void;
  resetPw: () => void;
  initialUsername?: string;
}) {
  return (
    <FrontPage isPhone={isPhone}>
      <LoginInputForm
        rememberMePreviousValue={rememberMePreviousValue}
        state={state}
        formAction={formAction}
        canBiometric={canBiometric}
        startBiometric={canBiometric ? startBiometric : undefined}
        resetPw={resetPw}
        isPhone={isPhone}
        initialUsername={initialUsername}
      />
    </FrontPage>
  );
}
